// road-signs.js — Verkehrsschilder als SVG für den Abschnitt „Unterwegs in <Land>“ (rein, ohne DOM).
// Je Straßenart ein Schild, wie es im Land aussieht (Farben aus SIGN_STYLES, Quellen in docs/road-signs.md),
// dazu das runde Tempo-Schild mit dem Wert der gewählten Fahrzeugklasse.
// Die Schilder sind vereinfacht gezeichnet (Form, Farben, Piktogramm); Ortsnamen stehen als Balken.
// Alle Zeichnungen sind aria-hidden — die Oberfläche nennt Straßenart und Wert als Text.

/** Schildfarben (Grundwerte). Länder überschreiben einzelne Teile in SIGN_STYLES. */
const BASE = {
  speed: { bg: '#ffffff', ring: '#c8102e', ink: '#1a1a1a', unit: null, mph: false },
  motorway: '#1f4e9c',
  expressway: '#1f4e9c',
  urban: { bg: '#ffffff', border: '#1a1a1a', ink: '#1a1a1a' },
};

/** Länderbesonderheiten (ISO → Teil-Stile). Nur, was vom Grundwert abweicht. */
const BLUE = '#1f4e9c', GREEN = '#00843d', YELLOW = '#f6c700', SIGN_YELLOW = '#ffd200', RED = '#c8102e', BLACK = '#1a1a1a', WHITE = '#ffffff';
const COMP = 'https://en.wikipedia.org/wiki/Comparison_of_European_road_signs';
const MW = 'https://de.wikipedia.org/wiki/Autobahn';
const black = { bg: WHITE, border: BLACK, ink: BLACK };
const red = { bg: WHITE, border: RED, ink: BLACK };
const yellow = { bg: YELLOW, border: BLACK, ink: BLACK };

/** Länderbesonderheiten (ISO → Teil-Stile). Nur, was vom Grundwert abweicht; Quellen in docs/road-signs.md.
 *  expresswayIcon 'motorway' = Schnellstraße zeigt das Autobahn-Piktogramm (Italien). */
export const SIGN_STYLES = {
  AT: { urban: { bg: WHITE, border: BLUE, ink: BLACK }, sources: ['https://www.ris.bka.gv.at/GeltendeFassung.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10011336', MW] },
  BE: { urban: black, sources: ['https://en.wikipedia.org/wiki/Road_signs_in_Belgium', MW] },
  CH: { motorway: GREEN, expressway: GREEN, urban: { bg: BLUE, border: WHITE, ink: WHITE }, sources: ['https://en.wikipedia.org/wiki/Road_signs_in_Switzerland_and_Liechtenstein', 'https://de.wikipedia.org/wiki/Ortstafel'] },
  CZ: { motorway: GREEN, urban: black, sources: [COMP, MW] },
  DE: { motorway: '#154889', expressway: '#154889', urban: yellow, advisory: { value: 130, color: '#154889' }, sources: ['https://www.gesetze-im-internet.de/stvo_2013/anlage_3.html', 'https://de.wikipedia.org/wiki/Richtgeschwindigkeit', 'https://commons.wikimedia.org/wiki/File:Zeichen_380_-_Richtgeschwindigkeit,_StVO_1992.svg'] },
  DK: { motorway: GREEN, urban: black, sources: ['https://commons.wikimedia.org/wiki/File:Denmark_road_sign_E55.svg', MW] },
  ES: { urban: red, sources: ['https://www.autofacil.es/senales-trafico/significado-color-senales-trafico/338146.html', MW] },
  FI: { speed: { bg: SIGN_YELLOW }, motorway: GREEN, expressway: GREEN, urban: yellow, sources: ['https://commons.wikimedia.org/wiki/File:Finland_road_sign_E22.svg', MW] },
  FR: { urban: red, sources: [COMP, MW] },
  GB: { speed: { mph: true }, expressway: null, urban: black, sources: ['https://en.wikipedia.org/wiki/Road_signs_in_the_United_Kingdom', 'https://commons.wikimedia.org/wiki/File:UK_traffic_sign_671.svg'] },
  GR: { motorway: GREEN, sources: ['https://en.wikipedia.org/wiki/Road_signs_in_Greece', MW] },
  HR: { motorway: GREEN, urban: yellow, sources: ['https://en.wikipedia.org/wiki/Road_signs_in_Croatia', MW] },
  HU: { urban: red, sources: ['https://commons.wikimedia.org/wiki/File:Hungary_road_sign_E-020.svg', MW] },
  IT: { motorway: GREEN, expresswayIcon: 'motorway', urban: black, sources: ['https://en.wikipedia.org/wiki/Road_signs_in_Italy', 'https://commons.wikimedia.org/wiki/File:Italian_traffic_signs_-_limiti_di_velocit%C3%A0_generali_(figura_II_352).svg'] },
  LU: { expressway: null, urban: yellow, sources: [COMP, MW] },
  NL: { urban: { bg: BLUE, border: WHITE, ink: WHITE }, sources: ['https://en.wikipedia.org/wiki/Road_signs_in_the_Netherlands', MW] },
  NO: { sources: ['https://en.wikipedia.org/wiki/Road_signs_in_Norway', 'https://commons.wikimedia.org/wiki/File:NO_road_sign_503.svg'] },
  PL: { urban: black, sources: ['https://commons.wikimedia.org/wiki/File:PL_road_sign_D-42.svg', MW] },
  PT: { urban: black, sources: ['https://en.wikipedia.org/wiki/Road_signs_in_Portugal', MW] },
  SE: { speed: { bg: SIGN_YELLOW }, motorway: GREEN, expressway: GREEN, urban: black, sources: ['https://commons.wikimedia.org/wiki/File:Sweden_road_sign_E5.svg', 'https://transportstyrelsen.se/TSFS/TSFS%202010_172.pdf'] },
  SI: { motorway: GREEN, urban: yellow, sources: ['https://www.uradni-list.si/glasilo-uradni-list-rs/vsebina/2026-01-0598/pravilnik-o-spremembah-pravilnika-o-prometni-signalizaciji-in-prometni-opremi-na-cestah', MW] },
  SK: { motorway: GREEN, urban: black, sources: ['https://en.wikipedia.org/wiki/Road_signs_in_Slovakia', MW] },
};

const deep = (a, b) => {
  const out = { ...a };
  for (const [k, v] of Object.entries(b || {})) out[k] = v && typeof v === 'object' && !Array.isArray(v) ? { ...(a[k] || {}), ...v } : v;
  return out;
};

/** Stil eines Landes (unbekanntes Land → Grundwerte). */
export function signStyle(iso) {
  return deep(BASE, Object.prototype.hasOwnProperty.call(SIGN_STYLES, iso) ? SIGN_STYLES[iso] : null);
}

const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
const svg = (body, label = '') => `<svg class="vc-sign" viewBox="0 0 100 100" aria-hidden="true" focusable="false"${label ? ` data-sign="${esc(label)}"` : ''}>${body}</svg>`;
const FONT = `font-family="'Roboto Flex', system-ui, sans-serif" font-weight="700" text-anchor="middle"`;

/** km/h → mph für Länder mit Meilen-Schildern (Werte stammen aus mph, z. B. 48 → 30). */
export const toMph = (kmh) => Math.round(kmh / 1.609344 / 5) * 5;

/** Zahl auf dem Tempo-Schild: km/h oder mph je Land. */
export function signNumber(kmh, style) {
  return style.speed.mph ? toMph(kmh) : kmh;
}

/** Rundes Tempo-Schild (Höchstgeschwindigkeit). */
export function speedSign(kmh, style) {
  const s = style.speed, n = String(signNumber(kmh, style));
  const size = n.length >= 3 ? 34 : 44, y = s.unit ? 47 : 52;
  return svg(`<circle cx="50" cy="50" r="49" fill="#ffffff" stroke="#9e9e9e" stroke-width="0.8"/>`
    + `<circle cx="50" cy="50" r="46" fill="${s.ring}"/><circle cx="50" cy="50" r="36" fill="${s.bg}"/>`
    + `<text x="50" y="${y}" ${FONT} font-size="${size}" dominant-baseline="central" fill="${s.ink}" letter-spacing="-1">${n}</text>`
    + (s.unit ? `<text x="50" y="70" ${FONT} font-size="10" fill="${s.ink}">${esc(s.unit)}</text>` : ''), 'speed-' + n);
}

/** Blaues Quadrat „Richtgeschwindigkeit“ (DE: Autobahn ohne allgemeines Limit). */
export function advisorySign(kmh, color) {
  return svg(`<rect x="3" y="3" width="94" height="94" rx="9" fill="#ffffff" stroke="#9e9e9e" stroke-width="0.8"/>`
    + `<rect x="6" y="6" width="88" height="88" rx="7" fill="${color}"/>`
    + `<text x="50" y="52" ${FONT} font-size="44" dominant-baseline="central" fill="#ffffff" letter-spacing="-1">${kmh}</text>`, 'advisory-' + kmh);
}

const panel = (color) => `<rect x="3" y="3" width="94" height="94" rx="9" fill="#ffffff" stroke="#9e9e9e" stroke-width="0.8"/>`
  + `<rect x="6" y="6" width="88" height="88" rx="7" fill="${color}"/><rect x="10" y="10" width="80" height="80" rx="5" fill="none" stroke="#ffffff" stroke-width="2"/>`;

/** Autobahn: zweibahnige Straße in Perspektive unter einer Brücke. */
export function motorwaySign(color, label = 'motorway') {
  return svg(panel(color)
    + `<rect x="17" y="27" width="66" height="8" fill="#ffffff"/><rect x="21" y="35" width="5" height="8" fill="#ffffff"/><rect x="74" y="35" width="5" height="8" fill="#ffffff"/>`
    + `<path d="M47 43 L45 43 L24 84 L41 84 Z M53 43 L55 43 L76 84 L59 84 Z" fill="#ffffff"/>`, label);
}

/** Schnellstraße / Kraftfahrstraße: Pkw von vorn-seitlich als Piktogramm. */
export function expresswaySign(color) {
  return svg(panel(color)
    + `<path d="M22 60 L26 48 Q28 42 34 41 L66 41 Q72 42 74 48 L78 60 Q81 61 81 65 L81 70 Q81 73 78 73 L22 73 Q19 73 19 70 L19 65 Q19 61 22 60 Z" fill="#ffffff"/>`
    + `<path d="M30 50 Q31 46 35 46 L65 46 Q69 46 70 50 L71 56 L29 56 Z" fill="${color}"/>`
    + `<rect x="24" y="73" width="10" height="7" rx="2" fill="#ffffff"/><rect x="66" y="73" width="10" height="7" rx="2" fill="#ffffff"/>`
    + `<circle cx="28" cy="64" r="3" fill="${color}"/><circle cx="72" cy="64" r="3" fill="${color}"/>`, 'expressway');
}

/** Ortsschild: Rechteck mit Rand, Ortsname als Balken. exit = Ortsende (rot durchgestrichen) für „außerorts“. */
export function townSign(style, exit = false) {
  const u = style.urban;
  return svg(`<rect x="3" y="17" width="94" height="66" rx="6" fill="#ffffff" stroke="#9e9e9e" stroke-width="0.8"/>`
    + `<rect x="6" y="20" width="88" height="60" rx="4" fill="${u.bg}"/><rect x="10" y="24" width="80" height="52" rx="3" fill="none" stroke="${u.border}" stroke-width="3"/>`
    + `<rect x="20" y="38" width="60" height="10" rx="2" fill="${u.ink}"/><rect x="30" y="55" width="40" height="6" rx="2" fill="${u.ink}" opacity="0.75"/>`
    + (exit ? `<path d="M14 72 L86 28" stroke="#c8102e" stroke-width="7" stroke-linecap="round"/>` : ''), exit ? 'town-exit' : 'town');
}

/** Schild für eine Straßenart (urban/rural/expressway/motorway) im Stil des Landes, oder null. */
export function roadSign(road, style) {
  if (road === 'urban') return townSign(style, false);
  if (road === 'rural') return townSign(style, true);
  if (road === 'expressway') return !style.expressway ? null : style.expresswayIcon === 'motorway' ? motorwaySign(style.expressway, 'expressway') : expresswaySign(style.expressway);
  if (road === 'motorway') return motorwaySign(style.motorway);
  return null;
}
